import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";

const COPY = "Thoughts, practical lessons and observations from the world of Estate Management — delivered occasionally.";

// The single, editorial email-capture block used across the site.
// tone: "light" (cream backgrounds) | "dark" (ink backgrounds)
// compact: condensed layout for the footer column
export const NewsletterSignup = ({ source = "newsletter", tone = "light", compact = false, withName = false, copy = COPY, className = "" }) => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [sending, setSending] = useState(false);
  const nav = useNavigate();
  const dark = tone === "dark";

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (!email) { toast.error("Please add your email."); return; }
    setSending(true);
    try {
      await api.post("/subscribe", { email, source, first_name: withName ? firstName : undefined });
      nav("/notes/welcome");
    } catch (err) { toast.error(apiError(err)); setSending(false); }
  };

  const kicker = dark ? "text-bg/50" : "text-accent";
  const body = dark ? "text-bg/70" : "text-ink-soft";
  const line = dark ? "border-bg/30 focus-within:border-bg" : "border-line focus-within:border-ink";
  const input = dark ? "text-bg placeholder:text-bg/40" : "text-ink placeholder:text-ink-soft/60";
  const btn = dark ? "text-bg hover:text-accent" : "text-ink hover:text-accent";

  if (compact) {
    return (
      <div data-testid={`newsletter-${source}`} className={className}>
        <p className={`text-[11px] uppercase tracking-ultra ${kicker}`}>Notes from Il Salotto</p>
        <p className={`mt-3 text-sm font-light max-w-xs ${body}`}>{copy}</p>
        <form onSubmit={submit} data-testid={`newsletter-form-${source}`} className={`mt-5 flex items-center gap-3 border-b transition-colors ${line}`}>
          <input
            data-testid={`newsletter-email-${source}`}
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className={`flex-1 min-w-0 bg-transparent outline-none py-3 font-light ${input}`}
            placeholder="you@email.com" aria-label="Email"
          />
          <button type="submit" disabled={sending} data-testid={`newsletter-submit-${source}`}
            className={`shrink-0 whitespace-nowrap text-[11px] uppercase tracking-[0.2em] transition-colors disabled:opacity-60 ${btn}`}>
            {sending ? "…" : "Join the Salotto"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid={`newsletter-${source}`} className={`max-w-2xl ${className}`}>
      <p className={`text-[11px] uppercase tracking-ultra ${kicker}`}>Notes from Il Salotto</p>
      <p className={`mt-6 font-serif font-light text-2xl lg:text-[2rem] leading-[1.3] tracking-tight ${dark ? "text-bg" : "text-ink"}`}>
        {copy}
      </p>
      {withName ? (
        <form onSubmit={submit} data-testid={`newsletter-form-${source}`} className="mt-9 max-w-md space-y-6">
          <div className={`border-b transition-colors ${line}`}>
            <input
              data-testid={`newsletter-firstname-${source}`}
              value={firstName} onChange={(e) => setFirstName(e.target.value)}
              className={`w-full bg-transparent outline-none py-3 text-lg font-light ${input}`}
              placeholder="First name" aria-label="First name"
            />
          </div>
          <div className={`flex items-center gap-4 border-b transition-colors ${line}`}>
            <input
              data-testid={`newsletter-email-${source}`}
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className={`flex-1 min-w-0 bg-transparent outline-none py-3 text-lg font-light ${input}`}
              placeholder="you@email.com" aria-label="Email"
            />
            <button type="submit" disabled={sending} data-testid={`newsletter-submit-${source}`}
              className={`group shrink-0 whitespace-nowrap inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] transition-colors disabled:opacity-60 ${btn}`}>
              {sending ? "…" : "Join the Salotto"}
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={submit} data-testid={`newsletter-form-${source}`} className={`mt-9 flex items-center gap-4 border-b transition-colors ${line}`}>
          <input
            data-testid={`newsletter-email-${source}`}
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className={`flex-1 min-w-0 bg-transparent outline-none py-3 text-lg font-light ${input}`}
            placeholder="you@email.com" aria-label="Email"
          />
          <button type="submit" disabled={sending} data-testid={`newsletter-submit-${source}`}
            className={`group shrink-0 whitespace-nowrap inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] transition-colors disabled:opacity-60 ${btn}`}>
            {sending ? "…" : "Join the Salotto"}
            <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
          </button>
        </form>
      )}
    </div>
  );
};
